import React, { memo, useCallback, useRef } from "react";
import { Select } from "@base-ui/react/select";
import "./sidebar-photo-panel.css";
import { SidebarPanel } from "../sidebar-panel/sidebar-panel";
import { useLabels } from "../../../hooks/useLabels";
import { useAddPhotoZone } from "../../../hooks/useAddPhotoZone";
import { usePhotoDragToZone } from "../../../hooks/use-photo-drag-to-zone";
import { useAppContext } from "../../../hooks/useAppContext";
import { labels } from "./labels";
import { useAutoFillButton } from "../../autofill-button";
import { PhotoGrid } from "../../photo-grid";
import { usePhotoSelection } from "../../bottom-photo-menus/photo-selection-panel/use-photo-selection";
import { usePhotoPreprocessProgress } from "../../../hooks/use-photo-preprocess-progress";
import { PhotoPreprocessProgress } from "../../photo-preprocess-progress";
import { Icon } from "../../icon";
import { useImportPhotosButton } from "../../import-photos-button/import-photos-button";
import { ImportPhotosPopover } from "../../import-photos-popover/import-photos-popover";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import { useSelectedZone } from "../../../hooks/useSelection";
import { IconButton } from "../../icon-button";
import { DragImage } from "../../drag-image";
import { Field } from "@base-ui/react/field";
import { usePhotoSort } from "../../../hooks/use-photo-sort";

const sortTypes = Object.values(eXpress.SortBy).map((sortType) => ({
	value: sortType,
	label: sortType,
}));

export const SidebarPhotoPanel = memo(function SidebarPhotoPanel() {
	const { googleAnalytics } = useAppContext();
	const dashboard = useDashboardContext();
	const selectedZone = useSelectedZone();
	const selectedPhotoZone = eXpress.core.isPhotoZone(selectedZone) ? selectedZone : undefined;

	const { canAddPhotoZoneToDefinition, canAddPhotoZoneToSpread, addPhotoZone } =
		useAddPhotoZone();

	const autoFillButton = useAutoFillButton();
	const importPhotosButton = useImportPhotosButton();
	const [{ sortBy, sortDirection }, { setSortBy, setSortDirection }] = usePhotoSort();

	/** Loading labels */
	const [[addPhotosText, addPhotosLongText, addPhotoZoneText, autoFillText], areLabelsLoading] =
		useLabels([
			labels.addPhotos,
			labels.addPhotosLong,
			canAddPhotoZoneToDefinition ? labels.addPhotoZone : undefined,
			labels.autofill,
		]);

	const onPhotoClick =
		selectedPhotoZone && dashboard.canApplyPhotoToZone(selectedPhotoZone)
			? (photo: eXpress.core.Photo) => {
					dashboard.applyPhotoToZone(selectedPhotoZone, photo);
			  }
			: undefined;

	const photos = usePhotoSelection();
	const sidebarPanelRef = useRef<HTMLDivElement>(null);
	const { getPhotoGestureHandlers, isDragging, dragImageRef } = usePhotoDragToZone({
		getContainerElement: useCallback(() => sidebarPanelRef.current, []),
	});

	const [photoPreprocessProgress] = usePhotoPreprocessProgress();

	// Preprocessing photos to upload?
	if (photoPreprocessProgress)
		return (
			<div className="sidebar-photo-panel__progress">
				<PhotoPreprocessProgress />
			</div>
		);

	if (areLabelsLoading) return null;

	return (
		<>
			<DragImage ref={dragImageRef} />
			<SidebarPanel>
				<div className="sidebar-photo-panel" ref={sidebarPanelRef}>
					<div className="sidebar-photo-panel__header">
						<div className="sidebar-photo-panel__import-photo">
							<ImportPhotosPopover
								onImportPhotos={importPhotosButton.onClick}
								render={(props) => (
									<button
										{...props}
										className="sidebar-photo-panel__header-button"
										disabled={importPhotosButton.disabled}
									>
										<Icon iconName={importPhotosButton.iconName} />
										{addPhotosText}
									</button>
								)}
							/>
						</div>

						<div className="sidebar-photo-panel__autofill">
							<button
								disabled={photos.length === 0}
								className="sidebar-photo-panel__header-button"
								onClick={() => {
									googleAnalytics.trackEventToGa({
										trackingAction: "AutofillClicked",
									});
									autoFillButton.onClick();
								}}
							>
								<Icon iconName={autoFillButton.iconName} />
								{autoFillText}
							</button>
							{autoFillButton.dialogElement}
						</div>

						{canAddPhotoZoneToDefinition && (
							<div className="sidebar-photo-panel__add-zone">
								<button
									className="sidebar-photo-panel__header-button"
									title={addPhotoZoneText}
									disabled={!canAddPhotoZoneToSpread}
									onClick={() => {
										addPhotoZone();
										googleAnalytics.trackEventToGa({
											trackingAction: "PhotoZoneAddClicked",
										});
									}}
								>
									<Icon iconName="add-photozone" />
									{addPhotoZoneText}
								</button>
							</div>
						)}
					</div>

					<div className="sidebar-photo-panel__photos-header">
						{/* TODO: SORTING, MULTI-SELECT? */}
						<Field.Root>
							<div className="sidebar-photo-panel__sort-row">
								<Select.Root
									value={sortBy}
									onValueChange={(value) => {
										setSortBy(value as eXpress.SortBy);
									}}
								>
									<Select.Trigger className="sidebar-photo-panel__select-trigger">
										<Select.Value>{sortBy}</Select.Value>
									</Select.Trigger>
									<Select.Portal>
										<Select.Positioner>
											<Select.Popup className="sidebar-photo-panel__select-popup">
												{sortTypes.map((sortType) => (
													<Select.Item
														key={sortType.value}
														value={sortType.value}
														className="sidebar-photo-panel__select-item"
													>
														<Select.ItemText>
															{sortType.label}
														</Select.ItemText>
													</Select.Item>
												))}
											</Select.Popup>
										</Select.Positioner>
									</Select.Portal>
								</Select.Root>
								<IconButton
									className={`sidebar-photo-panel__sort-direction${
										sortDirection === eXpress.SortDirection.Descending
											? " sidebar-photo-panel__sort-direction--descending"
											: ""
									}`}
									iconName="arrow-left"
									onClick={() => {
										setSortDirection(
											sortDirection === eXpress.SortDirection.Descending
												? eXpress.SortDirection.Ascending
												: eXpress.SortDirection.Descending,
										);
									}}
								/>
							</div>
						</Field.Root>
					</div>

					<div className="sidebar-photo-panel__photos">
						{photos.length ? (
							<PhotoGrid
								photos={photos}
								targetRowHeight={80}
								onClick={onPhotoClick}
								isDragging={isDragging}
								getPhotoGestureHandlers={getPhotoGestureHandlers}
								showRemoveButton={true}
							/>
						) : (
							<div className="sidebar-photo-panel__no-photos">
								<IconButton
									disabled={importPhotosButton.disabled}
									iconName={importPhotosButton.iconName}
									title={addPhotosLongText}
									onClick={importPhotosButton.onClick}
								/>
							</div>
						)}
					</div>
				</div>
			</SidebarPanel>
		</>
	);
});
